import React, { useEffect, useState } from 'react';
import { api, AED_short } from '../lib/api';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, MapPin, ChevronDown, ChevronRight, Home, Building2, Trees } from 'lucide-react';

// ---- Stages (workflow) ----
const STAGES = [
  { key: 'sourcing',    label: 'Sourcing',     tint: 'bg-slate-100 text-slate-700',    dot: 'bg-slate-400' },
  { key: 'under_offer', label: 'Under Offer',  tint: 'bg-amber-100 text-amber-800',    dot: 'bg-amber-500' },
  { key: 'owned',       label: 'Owned',        tint: 'bg-blue-100 text-blue-800',      dot: 'bg-blue-500' },
  { key: 'refurb',      label: 'Refurbishing', tint: 'bg-[#EAE3D9] text-amber-900',    dot: 'bg-[#B98A54]' },
  { key: 'listed',      label: 'Listed',       tint: 'bg-purple-100 text-purple-800',  dot: 'bg-purple-500' },
  { key: 'sold',        label: 'Sold',         tint: 'bg-emerald-100 text-emerald-800',dot: 'bg-emerald-500' },
  { key: 'rented',      label: 'Rented',       tint: 'bg-teal-100 text-teal-800',      dot: 'bg-teal-500' },
];

// ---- Property types ----
const PROPERTY_TYPES = [
  { v: 'apartment',         l: 'Apartment',         asset: 'residential' },
  { v: 'penthouse',         l: 'Penthouse',         asset: 'residential' },
  { v: 'townhouse',         l: 'Townhouse',         asset: 'residential' },
  { v: 'villa',             l: 'Villa',             asset: 'residential' },
  { v: 'commercial_office', l: 'Office',            asset: 'commercial' },
  { v: 'retail',            l: 'Retail',            asset: 'commercial' },
  { v: 'plot',              l: 'Land / Plot',       asset: 'land' },
];

const ASSET_CLASSES = [
  { key: 'residential', label: 'Residential', icon: Home,       tint: 'from-[#EEF2F0] to-[#DDE7E1]', accent: '#679286' },
  { key: 'commercial',  label: 'Commercial',  icon: Building2,  tint: 'from-[#EEEFF3] to-[#DDE0E7]', accent: '#556B7C' },
  { key: 'land',        label: 'Land / Plot', icon: Trees,      tint: 'from-[#F0EEE7] to-[#E5E1D2]', accent: '#93704A' },
];

function assetClassOf(p) {
  const t = PROPERTY_TYPES.find(x => x.v === p.property_type);
  return t?.asset || 'residential';
}

// ---- Score ring ----
function ScoreRing({ score }) {
  const v = Math.max(0, Math.min(100, score || 0));
  return (
    <div className="score-ring w-12 h-12" style={{ '--score': v }}>
      <div className="score-ring-inner w-9 h-9 text-xs font-serif">{Math.round(v)}</div>
    </div>
  );
}

// ---- Rental Yield summary strip ----
function RentalYieldStrip() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get('/properties/rental-yields')
      .then(r => setData(r.data))
      .catch(() => setData(null));
  }, []);
  if (!data || data.count === 0) return null;
  const withData = (data.properties || []).filter(p => p.has_data);
  if (withData.length === 0) return null;

  const rented = withData.filter(p => p.stage === 'rented');
  const totalAnnual = withData.reduce((s, p) => s + (p.annual_rent_aed || 0), 0);
  const totalCost   = withData.reduce((s, p) => s + (p.all_in_cost_aed || 0), 0);
  const portfolioGross = totalCost > 0 ? (totalAnnual / totalCost * 100) : 0;
  const netYields = withData.filter(p => p.net_yield_pct != null).map(p => p.net_yield_pct);
  const portfolioNet = netYields.length ? netYields.reduce((s, v) => s + v, 0) / netYields.length : 0;
  const best = withData.slice().sort((a, b) => (b.net_yield_pct || -1) - (a.net_yield_pct || -1))[0];

  return (
    <section className="mb-8 glass p-6" data-testid="rental-yield-strip">
      <div className="flex items-baseline justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="overline">Rental performance · portfolio</div>
          <div className="font-serif text-2xl mt-0.5">Held & rented assets</div>
        </div>
        <div className="text-xs text-slate-500">
          {rented.length} rented · {withData.length} with yield data
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StripTile label="Portfolio gross yield" value={`${portfolioGross.toFixed(2)}%`} accent="text-slate-900" testid="strip-gross"/>
        <StripTile label="Avg net yield" value={`${portfolioNet.toFixed(2)}%`} accent="text-teal-700" testid="strip-net"/>
        <StripTile label="Contracted annual rent" value={AED_short(totalAnnual)} accent="text-slate-900" testid="strip-rent"/>
        <StripTile label="All-in cost (assets tracked)" value={AED_short(totalCost)} accent="text-slate-900" testid="strip-cost"/>
      </div>
      {withData.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-slate-500 border-b border-slate-200/60">
                <th className="py-2 text-left">Property</th>
                <th className="text-left">Stage</th>
                <th className="text-right">Annual rent</th>
                <th className="text-right">All-in cost</th>
                <th className="text-right">Gross</th>
                <th className="text-right">Net</th>
              </tr>
            </thead>
            <tbody>
              {withData.map(p => (
                <tr key={p.id} className="border-b border-slate-100/70" data-testid={`yield-row-${p.id}`}>
                  <td className="py-2">
                    <Link to={`/app/properties/${p.id}`} className="font-medium text-slate-900 hover:text-[#679286]">
                      {p.title}
                    </Link>
                    <div className="text-[11px] text-slate-500">{p.community}{p.tenant_name ? ` · ${p.tenant_name}` : ''}</div>
                  </td>
                  <td className="text-xs capitalize text-slate-600">{p.stage}</td>
                  <td className="text-right">{AED_short(p.annual_rent_aed)}</td>
                  <td className="text-right">{AED_short(p.all_in_cost_aed)}</td>
                  <td className="text-right">{p.gross_yield_pct != null ? `${p.gross_yield_pct}%` : '—'}</td>
                  <td className={`text-right font-medium ${p.net_yield_pct >= 5 ? 'text-teal-700' : p.net_yield_pct >= 3 ? 'text-slate-900' : 'text-amber-700'}`}>
                    {p.net_yield_pct != null ? `${p.net_yield_pct}%` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {best && best.net_yield_pct != null && (
        <div className="text-xs text-slate-500 mt-3">
          Top performer: <span className="font-medium text-slate-800">{best.title}</span> · {best.net_yield_pct}% net
        </div>
      )}
    </section>
  );
}

function StripTile({ label, value, accent = 'text-slate-900', testid }) {
  return (
    <div className="rounded-2xl bg-white/70 border border-white/70 p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`font-serif text-xl mt-1 ${accent}`}>{value}</div>
    </div>
  );
}

// ---- Property card ----
function PropertyCard({ p, onDragStart }) {
  const purchase = p.purchase_price_aed || p.asking_price_aed;
  const roi = p.arv_aed && purchase
    ? (((p.arv_aed - (purchase + (p.refurb_budget_aed || 0))) / (purchase + (p.refurb_budget_aed || 0))) * 100).toFixed(0)
    : null;
  return (
    <Link to={`/app/properties/${p.id}`} data-testid={`property-card-${p.id}`}>
      <motion.div
        draggable
        onDragStart={(e) => onDragStart(e, p.id)}
        whileHover={{ y: -3 }}
        className="glass p-3 cursor-grab active:cursor-grabbing h-full"
      >
        {p.thumbnail_url && (
          <div className="h-24 rounded-xl overflow-hidden mb-3 bg-slate-200">
            <img src={p.thumbnail_url} alt="" className="w-full h-full object-cover" />
          </div>
        )}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="font-medium text-sm text-slate-900 truncate">{p.title}</div>
            <div className="flex items-center gap-1 text-xs text-slate-500 mt-1">
              <MapPin size={11}/> <span className="truncate">{p.community}</span>
            </div>
          </div>
          {p.deal_score != null && <ScoreRing score={p.deal_score} />}
        </div>
        <div className="flex items-center justify-between mt-3 text-xs gap-2">
          <div className="min-w-0">
            <div className="text-slate-500">Purchase</div>
            <div className="font-medium text-slate-800 truncate">{AED_short(purchase)}</div>
          </div>
          <div className="text-right min-w-0">
            <div className="text-slate-500">Sale value</div>
            <div className="font-medium text-slate-800 truncate">{AED_short(p.arv_aed)}</div>
          </div>
          {roi && (
            <div className="text-right min-w-0">
              <div className="text-slate-500">ROI</div>
              <div className={`font-semibold ${roi >= 15 ? 'text-emerald-600' : 'text-amber-600'}`}>{roi}%</div>
            </div>
          )}
        </div>
      </motion.div>
    </Link>
  );
}

// ---- Add property modal ----
function AddPropertyModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    title: '',
    property_type: 'apartment',
    community: 'Dubai Marina',
    building: '',
    bedrooms: 2, bathrooms: 2, size_sqft: 1200, plot_size_sqft: '',
    asking_price_aed: 2000000,
    listing_url: '', notes: '',
    stage: 'sourcing',
    view: '', floor: '',
    condition: 'good', furnished: 'unfurnished',
  });
  const [busy, setBusy] = useState(false);

  const ptype = PROPERTY_TYPES.find(t => t.v === form.property_type) || PROPERTY_TYPES[0];
  const isResidential = ptype.asset === 'residential';
  const isPlot = form.property_type === 'plot';
  const showPlotSize = ['villa', 'townhouse', 'plot'].includes(form.property_type);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // clean plot size / beds for commercial etc
      const payload = { ...form };
      if (isPlot) { payload.bedrooms = null; payload.bathrooms = null; payload.size_sqft = null; }
      if (!showPlotSize) payload.plot_size_sqft = null;
      const r = await api.post('/properties', payload);
      onCreated(r.data);
      onClose();
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-40 grid place-items-center p-4 bg-slate-900/30 backdrop-blur-sm" onClick={onClose}>
      <motion.form onClick={(e)=>e.stopPropagation()} onSubmit={submit}
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="glass-strong w-full max-w-2xl p-8 max-h-[90vh] overflow-y-auto" data-testid="add-property-modal"
      >
        <div className="overline mb-2">New</div>
        <h2 className="font-serif text-2xl mb-6">Add a property</h2>

        {/* Asset class tabs */}
        <div className="mb-4">
          <div className="overline text-xs mb-2">Asset class</div>
          <div className="grid grid-cols-3 gap-2">
            {ASSET_CLASSES.map(a => {
              const active = ptype.asset === a.key;
              const firstType = PROPERTY_TYPES.find(t => t.asset === a.key);
              const Icon = a.icon;
              return (
                <button
                  key={a.key}
                  type="button"
                  data-testid={`asset-class-${a.key}`}
                  onClick={() => setForm({ ...form, property_type: firstType.v })}
                  className={`px-3 py-3 rounded-2xl border transition-all flex items-center justify-center gap-2 text-sm ${
                    active
                      ? 'bg-[#1A2530] text-white border-[#1A2530] shadow-md'
                      : 'bg-white/60 text-slate-700 border-slate-200 hover:bg-white'
                  }`}
                >
                  <Icon size={14}/> {a.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <label className="col-span-2">Property type
            <select
              value={form.property_type}
              onChange={(e)=>setForm({...form, property_type:e.target.value})}
              data-testid="new-property-type"
              className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"
            >
              {PROPERTY_TYPES.filter(t => t.asset === ptype.asset).map(t => (
                <option key={t.v} value={t.v}>{t.l}</option>
              ))}
            </select>
          </label>

          <label className="col-span-2">Title
            <input required data-testid="new-property-title" value={form.title} onChange={(e)=>setForm({...form, title:e.target.value})}
                   placeholder="e.g. Marina Gate 1 — 2BR high-floor marina view"
                   className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
          </label>
          <label>Community
            <input value={form.community} onChange={(e)=>setForm({...form, community:e.target.value})} className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
          </label>
          <label>Building / Project
            <input value={form.building} onChange={(e)=>setForm({...form, building:e.target.value})} className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
          </label>

          {isResidential && !isPlot && (
            <>
              <label>Beds
                <input type="number" value={form.bedrooms} onChange={(e)=>setForm({...form, bedrooms:+e.target.value})} className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
              </label>
              <label>Baths
                <input type="number" step="0.5" value={form.bathrooms} onChange={(e)=>setForm({...form, bathrooms:+e.target.value})} className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
              </label>
            </>
          )}
          {!isPlot && (
            <label className={isResidential ? '' : 'col-span-2'}>Built-up (sqft)
              <input type="number" value={form.size_sqft} onChange={(e)=>setForm({...form, size_sqft:+e.target.value})} className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
            </label>
          )}
          {showPlotSize && (
            <label className={isPlot ? 'col-span-2' : ''}>Plot size (sqft)
              <input type="number" value={form.plot_size_sqft} onChange={(e)=>setForm({...form, plot_size_sqft:+e.target.value})} className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
            </label>
          )}

          {!isPlot && (
            <>
              <label>View / orientation
                <input value={form.view} onChange={(e)=>setForm({...form, view:e.target.value})} placeholder="Marina, Golf, Burj…" className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
              </label>
              <label>Floor
                <input value={form.floor} onChange={(e)=>setForm({...form, floor:e.target.value})} placeholder="e.g. 12 / high" className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
              </label>
              <label>Condition
                <select value={form.condition} onChange={(e)=>setForm({...form, condition:e.target.value})} className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80">
                  <option value="excellent">Excellent — move-in ready</option>
                  <option value="good">Good — light refresh only</option>
                  <option value="needs_refresh">Needs refresh — paint / soft finishes</option>
                  <option value="needs_refurb">Needs full refurb</option>
                  <option value="distressed">Distressed — down to shell</option>
                </select>
              </label>
              <label>Furnishing
                <select value={form.furnished} onChange={(e)=>setForm({...form, furnished:e.target.value})} className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80">
                  <option value="furnished">Furnished</option>
                  <option value="semi_furnished">Semi-furnished</option>
                  <option value="unfurnished">Unfurnished</option>
                </select>
              </label>
            </>
          )}

          <label className="col-span-2">Asking price (AED)
            <input type="number" value={form.asking_price_aed} onChange={(e)=>setForm({...form, asking_price_aed:+e.target.value})} className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
          </label>
          <label className="col-span-2">Listing URL (Bayut / PF)
            <input value={form.listing_url} onChange={(e)=>setForm({...form, listing_url:e.target.value})} placeholder="https://…" className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
          </label>
          <label className="col-span-2">Stage
            <select value={form.stage} onChange={(e)=>setForm({...form, stage:e.target.value})} data-testid="new-property-stage"
                    className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80">
              <option value="sourcing">Sourcing (analyzing / hunting)</option>
              <option value="under_offer">Under Offer (MOU signed / negotiating)</option>
              <option value="owned">Owned (closed, pre-refurb)</option>
              <option value="refurb">Refurbishing</option>
              <option value="listed">Listed for sale</option>
              <option value="sold">Sold</option>
            </select>
          </label>
          <label className="col-span-2">Notes
            <textarea rows={2} value={form.notes} onChange={(e)=>setForm({...form, notes:e.target.value})} className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
          </label>
        </div>
        <div className="flex gap-2 justify-end mt-6">
          <button type="button" onClick={onClose} className="btn-ghost text-sm">Cancel</button>
          <button disabled={busy} type="submit" className="btn-primary text-sm" data-testid="create-property-submit">{busy ? 'Creating…' : 'Create property'}</button>
        </div>
      </motion.form>
    </div>
  );
}

// ---- Stage row inside an asset section ----
function StageRow({ stage, items, onDragStart, onDrop }) {
  return (
    <div
      onDragOver={(e)=>e.preventDefault()}
      onDrop={(e)=>onDrop(e, stage.key)}
      className="mb-8"
      data-testid={`stage-row-${stage.key}`}
    >
      <div className="flex items-baseline gap-4 mb-4 pb-2 border-b border-slate-200/60">
        <span className={`w-2 h-2 rounded-full ${stage.dot}`}/>
        <h3 className="font-serif text-2xl md:text-3xl text-slate-900 leading-none">
          {stage.label}
        </h3>
        <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wider ${stage.tint}`}>
          {items.length}
        </span>
        <div className="flex-1"/>
        <span className="text-xs text-slate-400 hidden md:inline">drag cards here</span>
      </div>
      {items.length === 0 ? (
        <div className="text-center text-xs text-slate-400 py-6 border border-dashed border-slate-200 rounded-2xl">
          Drop a card here to move it to <b>{stage.label}</b>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {items.map(p => <PropertyCard key={p.id} p={p} onDragStart={onDragStart} />)}
        </div>
      )}
    </div>
  );
}

// ---- Asset class section (collapsible) ----
function AssetSection({ cls, propsList, onDragStart, onDrop, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  const Icon = cls.icon;
  const items = propsList.filter(p => assetClassOf(p) === cls.key);
  if (items.length === 0) return null;

  return (
    <section className="mb-8" data-testid={`asset-section-${cls.key}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center gap-3 p-4 rounded-2xl bg-gradient-to-r ${cls.tint} border border-white/60 shadow-sm hover:shadow transition-all`}
        data-testid={`asset-toggle-${cls.key}`}
      >
        <div className="w-10 h-10 rounded-full grid place-items-center bg-white/70">
          <Icon size={18} style={{ color: cls.accent }}/>
        </div>
        <div className="flex-1 text-left">
          <div className="font-serif text-2xl">{cls.label}</div>
          <div className="text-xs text-slate-600 mt-0.5">{items.length} {items.length === 1 ? 'property' : 'properties'} · grouped by pipeline stage</div>
        </div>
        {open ? <ChevronDown size={20} className="text-slate-500"/> : <ChevronRight size={20} className="text-slate-500"/>}
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="pt-5 pl-2">
              {STAGES.map(s => (
                <StageRow
                  key={s.key}
                  stage={s}
                  items={items.filter(p => p.stage === s.key)}
                  onDragStart={onDragStart}
                  onDrop={onDrop}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

// ---- Main page ----
export default function Properties() {
  const [props, setProps] = useState([]);
  const [adding, setAdding] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get('/properties').then(r => { setProps(r.data); setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  const onDragStart = (e, id) => { e.dataTransfer.setData('id', id); };
  const onDropStage = async (e, stage) => {
    e.preventDefault();
    const id = e.dataTransfer.getData('id');
    if (!id) return;
    if (stage === 'rented') {
      // Rented requires tenant + lease + rent — route to PropertyDetail's
      // "Mark as rented" flow instead of a silent stage change.
      window.location.assign(`/app/properties/${id}?openRent=1`);
      return;
    }
    setProps(props.map(p => p.id === id ? { ...p, stage } : p));
    await api.post(`/properties/${id}/stage`, { stage });
  };

  const total = props.length;
  const byAsset = {
    residential: props.filter(p => assetClassOf(p) === 'residential').length,
    commercial:  props.filter(p => assetClassOf(p) === 'commercial').length,
    land:        props.filter(p => assetClassOf(p) === 'land').length,
  };

  return (
    <div className="py-6 max-w-[1400px] mx-auto" data-testid="properties-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-0.5">Portfolio</div>
          <h1 className="font-serif text-4xl">Assets</h1>
          {!loading && (
            <div className="flex gap-4 mt-2 text-xs text-slate-500">
              <span>{total} total</span>
              <span>·</span>
              <span className="flex items-center gap-1"><Home size={11}/> {byAsset.residential} residential</span>
              <span className="flex items-center gap-1"><Building2 size={11}/> {byAsset.commercial} commercial</span>
              <span className="flex items-center gap-1"><Trees size={11}/> {byAsset.land} land</span>
            </div>
          )}
        </div>
        <button onClick={() => setAdding(true)} data-testid="add-property-btn" className="btn-primary text-sm flex items-center gap-2">
          <Plus size={14}/> Add property
        </button>
      </div>

      {loading ? (
        <div className="text-slate-500 py-10">Loading…</div>
      ) : props.length === 0 ? (
        <div className="glass p-12 text-center">
          <div className="text-slate-500 mb-4">No properties yet. Add your first property to see it here.</div>
          <button onClick={() => setAdding(true)} className="btn-primary text-sm inline-flex items-center gap-2">
            <Plus size={14}/> Add your first property
          </button>
        </div>
      ) : (
        <div>
          <RentalYieldStrip/>
          {ASSET_CLASSES.map(cls => (
            <AssetSection
              key={cls.key}
              cls={cls}
              propsList={props}
              onDragStart={onDragStart}
              onDrop={onDropStage}
            />
          ))}
        </div>
      )}

      {adding && <AddPropertyModal onClose={() => setAdding(false)} onCreated={() => load()} />}
    </div>
  );
}
